// Generated by tutorials/record.cjs from tutorials/articles/h54-diet-configuration.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h54 --text-only` (wording) or
// `node tutorials/record.cjs h54` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'diet-configuration',
  catalogId: 'h54',
  title: 'Diet configuration: diet types and consistencies',
  summary: 'Set up the diet types and consistencies your diet orders are made of: their names, abbreviations and label colors, how consistencies combine, the order they are written in, and what to check before you change one.',
  audience: 'Dietary managers and menu planners who keep the facility\'s diet lists',
  area: 'food-data',
  learn: [
    'What Diet Configuration opens',
    'Set a diet type\'s name, abbreviation and label colors',
    'What each consistency option does',
    'How consistency combinations work',
    'Set the order diet types and consistencies are written in on a diet order',
    'Check who and what uses a diet type before you change it',
  ],
  keywords: [
    'diet configuration',
    'diet config',
    'diet type',
    'diet types',
    'diet',
    'consistency',
    'consistencies',
    'texture',
    'liquid',
    'thickener',
    'thickened liquids',
    'consistency combination',
    'combination',
    'diet list sequence',
    'sequence',
    'diet order order',
    'label color',
    'label colors',
    'abbreviation',
    'usage',
    'replace diet type',
    'rename diet',
  ],
  steps: [
    {
      id: 'hub',
      part: 'Open Diet Configuration',
      heading: 'Open Diet Configuration',
      text: 'Select Food Data in the menu on the left, then Diet Configuration.\n\nIt gathers the tables your diet orders are built from in one place. Under Diet Tables: Diet Types, Consistencies and Food Groups. Under Resident Tables: Food Allergies, Special Needs and Special Devices. Each link opens that table on the Tables page.',
      media: {
        image: {
          src: '/help/diet-configuration/hub.webp',
          width: 1280,
          height: 842,
        },
        alt: 'Diet Configuration with two lists of links: Diet Tables (Diet Types, Consistencies, Food Groups) and Resident Tables (Food Allergies, Special Needs, Special Devices).',
      },
    },
    {
      id: 'diet-types',
      part: 'Diet types',
      heading: 'Your diet types',
      text: 'Select Diet Types. The list shows every diet type in its label colors, with its Abbreviation.\n\nThese are the diets you pick from on a resident\'s diet order and that your menu cycle is written for. Every facility in your organization shares the same list.',
      media: {
        image: {
          src: '/help/diet-configuration/diet-types.webp',
          width: 1280,
          height: 990,
        },
        clip: {
          src: '/help/diet-configuration/diet-types.mp4',
          width: 1280,
          height: 990,
          durationSec: 3.4,
        },
        alt: 'The Diet Types table: Cardiac, Consistent Carb, Finger Foods, Liberal Geriatric, Low Sodium 2g, Regular and Renal, each in its own colors, with abbreviations such as CAR and REN.',
      },
    },
    {
      id: 'open-diet',
      part: 'Diet types',
      heading: 'Open a diet type',
      text: 'Double-click a diet type, or select it and then Open. The Edit Diet Type box opens.\n\nThe Diet Type name can be up to 17 characters. The Abbreviation, up to three, is used on reports and labels where space is tight.',
      media: {
        image: {
          src: '/help/diet-configuration/open-diet.webp',
          width: 944,
          height: 844,
        },
        alt: 'The Edit Diet Type box for Renal: Diet Type Renal, Abbreviation REN, a yellow Background and a dark Font Color, and the Example label in those colors.',
      },
    },
    {
      id: 'colors',
      part: 'Diet types',
      heading: 'Pick the label colors',
      text: 'Background and Font Color are the diet\'s label colors, and Example shows how the label looks. Pick a color with each box.\n\nThe colors show in lists such as this one and on tray tickets and labels that print in color, so a diet can be spotted at a glance.',
      media: {
        image: {
          src: '/help/diet-configuration/colors.webp',
          width: 944,
          height: 844,
        },
        clip: {
          src: '/help/diet-configuration/colors.mp4',
          width: 944,
          height: 844,
          durationSec: 3.8,
        },
        alt: 'The Background changed to a light orange, and the Example label Renal now shows in light orange.',
      },
    },
    {
      id: 'options',
      part: 'Diet types',
      heading: 'The two tray ticket options',
      text: 'Bulk - Elevate to Tray Ticket: when Bulk 1st Choice is on in your settings, residents on this diet get a tray ticket instead of a bulk order.\n\nPrevent Not on Menu Cycle Warning: when this diet is on a diet order but not in the menu cycle, the tray ticket does not print its "Diet Type(s) not on Menu" warning.\n\nBoth show only when your facility uses production menus.',
      media: {
        image: {
          src: '/help/diet-configuration/options.webp',
          width: 944,
          height: 844,
        },
        alt: 'The two checkboxes Bulk - Elevate to Tray Ticket and Prevent Not on Menu Cycle Warning, both unchecked.',
      },
    },
    {
      id: 'save-diet',
      part: 'Diet types',
      heading: 'Save',
      text: 'Select Save. The list shows the diet type with its new colors, and so does every diet order and tray ticket that has it.',
      media: {
        image: {
          src: '/help/diet-configuration/save-diet.webp',
          width: 1280,
          height: 990,
        },
        clip: {
          src: '/help/diet-configuration/save-diet.mp4',
          width: 1280,
          height: 990,
          durationSec: 3.5,
        },
        alt: 'The Diet Types table with Renal now in light orange.',
      },
    },
    {
      id: 'consistencies',
      part: 'Consistencies',
      heading: 'Your consistencies',
      text: 'Select Consistencies in the list on the left. These are your food textures and liquid thicknesses, such as Mechanical Soft, Pureed or Mildly Thick Liquids.\n\nUsed as Thickener marks the thickened liquids. Food Only marks the textures that apply to food, not to drinks.',
      media: {
        image: {
          src: '/help/diet-configuration/consistencies.webp',
          width: 1280,
          height: 990,
        },
        clip: {
          src: '/help/diet-configuration/consistencies.mp4',
          width: 1280,
          height: 990,
          durationSec: 3.1,
        },
        alt: 'The Consistencies table with Used as Thickener checked for Mildly and Moderately Thick Liquids, and Food Only checked for Mechanical Soft, Minced and Moist, Pureed and Regular.',
      },
    },
    {
      id: 'open-consist',
      part: 'Consistencies',
      heading: 'Open a consistency',
      text: 'Double-click a consistency to open it. Like a diet type, it has a name of up to 17 characters, a three-letter Abbreviation and label colors.\n\nWhen your facility uses production menus you also see: Can be used as Thickener (a thickened liquid), Food Only (a texture for food, not drinks), Highlight Item on Tray Ticket (its items stand out on the tray ticket) and Bulk - Elevate to Tray Ticket.',
      media: {
        image: {
          src: '/help/diet-configuration/open-consist.webp',
          width: 944,
          height: 1128,
        },
        clip: {
          src: '/help/diet-configuration/open-consist.mp4',
          width: 944,
          height: 1128,
          durationSec: 2,
        },
        alt: 'The Edit Consistency box for Minced and Moist: Abbreviation MM, label colors, Food Only checked, and the Highlight Item on Tray Ticket and Bulk - Elevate to Tray Ticket options.',
      },
    },
    {
      id: 'combinations',
      part: 'How consistencies combine',
      heading: 'Consistency combinations',
      text: 'Select Consistency Combinations. Each row names a Combination Consistency and the two to five consistencies it stands for.\n\nWhen a resident\'s diet order has exactly those consistencies, such as Pureed and Mildly Thick Liquids, the tray ticket uses the menu written for the combination, here Pureed Mild Thick. Add the combination consistency on Consistencies first, then give it a menu in the menu cycle.',
      media: {
        image: {
          src: '/help/diet-configuration/combinations.webp',
          width: 1280,
          height: 990,
        },
        clip: {
          src: '/help/diet-configuration/combinations.mp4',
          width: 1280,
          height: 990,
          durationSec: 3.2,
        },
        alt: 'The Consistency Combinations table: Pureed Mild Thick is Pureed and Mildly Thick Liquids, and Pureed Mod Thick is Pureed and Moderately Thick Liquids.',
      },
    },
    {
      id: 'new-combination',
      part: 'How consistencies combine',
      heading: 'Add a combination',
      text: 'Select New. Pick the Combination Consistency, then the consistencies it stands for: Consistency 1 and 2 are required, 3 to 5 are for longer combinations and are filled in order, with no gaps. Select Save.',
      media: {
        image: {
          src: '/help/diet-configuration/new-combination.webp',
          width: 944,
          height: 728,
        },
        clip: {
          src: '/help/diet-configuration/new-combination.mp4',
          width: 944,
          height: 728,
          durationSec: 2,
        },
        alt: 'The Add New Consistency Combination box with Combination Consistency, Consistency 1 and Consistency 2 marked required, and Consistency 3 to 5.',
      },
    },
    {
      id: 'sequence',
      part: 'How consistencies combine',
      heading: 'The order on a diet order',
      text: 'Select Diet List Sequence. It sets the order diet types and consistencies are written in on a diet order and on the tray ticket. Use the arrows to move the selected row up or down; Fill starts the list from your menu cycle.\n\nEvery diet order entered or received after that is arranged in this order. Save also rewrites the diet order of every current resident to match, so plan the order before you save. Left empty, diet orders keep the order they were entered in.',
      media: {
        image: {
          src: '/help/diet-configuration/sequence.webp',
          width: 1280,
          height: 990,
        },
        clip: {
          src: '/help/diet-configuration/sequence.mp4',
          width: 1280,
          height: 990,
          durationSec: 2.6,
        },
        alt: 'Diet List Sequence: Diet Types in order Regular, Liberal Geriatric, Cardiac, Consistent Carb, Low Sodium 2g, Renal, Finger Foods, and Consistencies from Regular to Moderately Thick Liquids.',
      },
    },
    {
      id: 'combine-done',
      part: 'How consistencies combine',
      heading: 'Check a diet order',
      text: 'After a change here, open a resident whose diet order uses it and check that the diet order and the tray ticket read the way you expect.',
      textOnly: true,
    },
    {
      id: 'usage',
      part: 'Before you change a diet type',
      heading: 'See where it is used',
      text: 'Before you change a diet type, select it, then More actions (the three dots at the top right) and Usage.\n\nSystem Wide Item Usage lists every resident whose diet order has it, under their facility, with the date of the diet order, and every menu cycle diet order written for it.',
      media: {
        image: {
          src: '/help/diet-configuration/usage.webp',
          width: 1280,
          height: 444,
        },
        clip: {
          src: '/help/diet-configuration/usage.mp4',
          width: 1280,
          height: 444,
          durationSec: 2,
        },
        alt: 'System Wide Item Usage for Renal: the Fall/Winter Cycle 2026 menu cycle diet order, and three residents at Maple Grove Care Center with their diet order dates.',
      },
    },
    {
      id: 'rename',
      part: 'Before you change a diet type',
      heading: 'Renaming a diet type',
      text: 'A diet type\'s name, abbreviation and colors are shown wherever it is used, so a change shows at once on residents\' diet orders, in the menu cycle and on the next tray tickets.\n\nWhen the diet type is used in your menu cycle, Save asks "The selected diet type is used in the default menu cycle, would you like to change the name anyway?" Select Yes to rename it, or No to go back.',
      media: {
        image: {
          src: '/help/diet-configuration/rename.webp',
          width: 708,
          height: 322,
        },
        clip: {
          src: '/help/diet-configuration/rename.mp4',
          width: 708,
          height: 322,
          durationSec: 4.5,
        },
        alt: 'The Diet Type In Use box asking whether to change the name anyway, with No and Yes.',
      },
    },
    {
      id: 'replace',
      part: 'Before you change a diet type',
      heading: 'Replace, History and Export',
      text: 'More actions also has:\n\n- History (Selected Item): who changed the selected diet type, when, and what changed. History (Deleted Diet Types) lists the ones removed.\n- Replace: moves every resident, menu and setting from the selected diet type to the one you pick, then removes it. Use it to merge two diets that mean the same thing.\n- Export as CSV or Excel.\n- Delete, for a diet type nothing uses.\n\nReplace and Delete need the highest Diet Type level.',
      media: {
        image: {
          src: '/help/diet-configuration/replace.webp',
          width: 430,
          height: 480,
        },
        clip: {
          src: '/help/diet-configuration/replace.mp4',
          width: 430,
          height: 480,
          durationSec: 2.3,
        },
        alt: 'The More actions menu on Diet Types: Usage, History (Selected Item), History (Deleted Diet Types), Replace, Export as CSV, Export as Excel and Delete.',
      },
    },
    {
      id: 'shared',
      part: 'Before you change a diet type',
      heading: 'One list for every facility',
      text: 'Diet types and consistencies are shared by every facility in your organization. A new name or abbreviation, a Replace or a Delete reaches all of them, so check Usage first and tell the other facilities before you change one.\n\nWhen your organization has more than one facility, Override in the edit box lets one facility use its own label colors.',
      textOnly: true,
    },
  ],
  tips: [
    'Before you rename, recolor or replace a diet type, select it and use More actions, Usage to see which residents and menus use it.',
    'Keep abbreviations short and clear: reports and labels use them where space is tight.',
    'Diet Configuration only gathers the diet and resident tables in one place. You can open the same tables from Food Data, Tables.',
  ],
  notes: [
    'Diet types and consistencies are shared by every facility in your organization: a new name or abbreviation shows at all of them. When your organization has more than one facility, Override in the edit box lets one facility use its own label colors.',
    'Changing a name, abbreviation or color changes it everywhere at once: the residents\' diet orders, the menu cycle and the next tray tickets you print. Nothing has to be re-entered.',
    'Opening a table needs that table\'s level on your role: Diet Type, Consistency and so on. Changing a row needs level 2, adding one level 3, and Delete and Replace level 4. Diet List Sequence needs Diet Fluctuations level 4. If a button is missing, ask your administrator.',
    'If your Consistencies list shows IDDSI Level and IDDSI Type, pick the level each consistency matches.',
    'Food Allergies, Special Needs and Special Devices, the resident tables on Diet Configuration, are covered in Tables.',
  ],
  related: [
    'tables',
    'change-diet-order',
    'diet-and-consistency-rules',
    'tray-tickets',
    'menu-cycles',
    'roles-and-access',
  ],
  recorded: '2026-11-03',
};

export default article;
